// The Summary tab's From – To range ⇄ the reports' variables.
import { formatDateInput } from '@/lib/format/date';
import { shiftDateInput } from '@/lib/utils';

// A period's end is the next one's start (iscc-backend's [start, end) windows), so its last day is end − 1.
export const periodRange = (period) => ({
  from: formatDateInput(period?.startDate),
  to: shiftDateInput(formatDateInput(period?.endDate), -1),
});

// Dates compare as yyyy-MM-dd strings; an equal From and To is one whole day.
export const isRangeInvalid = ({ from, to }) => !from || !to || to < from;

// The reports still key on a period (persisted physical counts hang off it): the one From falls in.
export const periodContaining = (periods, from) =>
  periods.find((period) => {
    const range = periodRange(period);
    return range.from && range.to && range.from <= from && from <= range.to;
  }) ?? periods[0];

// Figma's default range: the mass balance period running today, else the first one.
export const currentPeriodRange = (periods, today) =>
  periodRange(
    periods.find((period) => {
      const range = periodRange(period);
      return range.from && range.to && range.from <= today && today <= range.to;
    }) ?? periods[0],
  );

/** The reports' variables, or null when the range can't be run; To travels as the day after. */
export const buildSummaryReportArgs = ({ item, periods, range }) => {
  if (isRangeInvalid(range)) return null;
  const anchor = periodContaining(periods, range.from);
  if (!anchor) return null;
  return {
    siteId: item?.facilityId,
    materialId: item?.materialId,
    mbPeriodDetailId: anchor.value,
    terminalId: item?.terminalId ?? undefined,
    fromDate: range.from,
    toDate: shiftDateInput(range.to, 1),
  };
};

/**
 * Reconciliation's variables. A range that is exactly one period runs without dates, so the rows
 * are that period's saved sub-periods — the only rows a physical balance can be saved against.
 */
export const buildReconciliationArgs = ({ item, periods, range }) => {
  const args = buildSummaryReportArgs({ item, periods, range });
  if (!args) return null;
  const anchor = periodContaining(periods, range.from);
  const whole = periodRange(anchor);
  if (whole.from !== range.from || whole.to !== range.to) return args;
  return { ...args, fromDate: undefined, toDate: undefined };
};
